import * as React from 'react';
import { styled } from '@mui/material/styles';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch, { SwitchProps } from '@mui/material/Switch';
import { Tooltip } from '@mui/material';

const ToggleButton = styled(
	(
		props: SwitchProps & {
			scale: number;
		}
	) => (
		<Switch
			focusVisibleClassName=".Mui-focusVisible"
			disableRipple
			{...props}
		/>
	)
)(({ theme, scale }) => {
	const width = 42 * scale;
	const height = 26 * scale;
	const thumb = 22 * scale;
	const margin = 2 * scale;

	return {
		width,
		height,
		padding: 0,
		'& .MuiSwitch-switchBase': {
			padding: 0,
			margin,
			transitionDuration: '300ms',
			'&.Mui-checked': {
				transform: `translateX(${width - thumb - margin * 2}px)`,
				color: '#fff',
				'& + .MuiSwitch-track': {
					backgroundColor: '#65C466',
					opacity: 1,
					border: 0,
					...theme.applyStyles('dark', {
						backgroundColor: '#2ECA45',
					}),
				},
				'&.Mui-disabled + .MuiSwitch-track': {
					opacity: 0.5,
				},
			},
			'&.Mui-focusVisible .MuiSwitch-thumb': {
				color: '#33cf4d',
				border: '6px solid #fff',
			},
			'&.Mui-disabled .MuiSwitch-thumb': {
				color: theme.palette.gray[100],
				...theme.applyStyles('dark', {
					color: theme.palette.gray[600],
				}),
			},
			'&.Mui-disabled + .MuiSwitch-track': {
				opacity: 0.7,
				...theme.applyStyles('dark', {
					opacity: 0.3,
				}),
			},
		},
		'& .MuiSwitch-thumb': {
			boxSizing: 'border-box',
			width: thumb,
			height: thumb,
		},
		'& .MuiSwitch-track': {
			borderRadius: 26 / 2,
			backgroundColor: theme.palette.gray[300],
			opacity: 1,
			transition: theme.transitions.create(['background-color'], {
				duration: 500,
			}),
			...theme.applyStyles('dark', {
				backgroundColor: '#39393D',
			}),
		},
	};
});

const CustomSwitch: React.FC<{
	onChange: (e) => void;
	on: boolean;
	disabled?: boolean;
	disabledTooltip?: string;
	size?: 'extra-small' | 'small' | 'medium' | 'large';
}> = ({ onChange, on, disabled, disabledTooltip, size = 'mmedium' }) => {
	let scale = 1;

	switch (size) {
		case 'extra-small':
			scale = 0.5;
			break;
		case 'small':
			scale = 0.7;
			break;
		case 'medium':
			scale = 1;
			break;
		case 'large':
			scale = 1.5;
			break;
		default:
			break;
	}

	const top = { 'extra-small': -5, small: -9, medium: -12, large: -12 };

	return (
		<FormGroup sx={{ position: 'relative' }}>
			<Tooltip title={disabledTooltip}>
				<FormControlLabel
					sx={{ position: 'absolute', right: 10, top: top[size] }}
					control={
						<ToggleButton checked={on} onChange={onChange} scale={scale} />
					}
					label=""
					labelPlacement="start"
					disabled={disabled}
				/>
			</Tooltip>
		</FormGroup>
	);
};

export default CustomSwitch;
